import { doc, getDoc, setDoc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from './firebase';

export interface LiveChatSettings {
  enabled: boolean;
  websiteId: string;
  desktopEnabled: boolean;
  mobileEnabled: boolean;
  position: 'left' | 'right';
  themeColor: string;
  welcomeMessage: string;
}

export const DEFAULT_LIVE_CHAT_SETTINGS: LiveChatSettings = {
  enabled: true,
  websiteId: 'ad7c0aed-7a19-4c04-ac68-8ac0c5d673fb',
  desktopEnabled: true,
  mobileEnabled: true,
  position: 'right',
  themeColor: 'purple',
  welcomeMessage: 'Hi there! 👋\nWelcome to RH Editography. How can we help you today?'
};

const COLLECTION_NAME = 'site_settings';
const DOC_ID = 'live_chat';

let cachedSettings: LiveChatSettings = { ...DEFAULT_LIVE_CHAT_SETTINGS };

try {
  const saved = localStorage.getItem('rh_live_chat_settings');
  if (saved) {
    cachedSettings = { ...DEFAULT_LIVE_CHAT_SETTINGS, ...JSON.parse(saved) };
  }
} catch (e) {}

export function getLocalLiveChatSettings(): LiveChatSettings {
  return cachedSettings;
}

export async function fetchLiveChatSettingsFromFirestore(): Promise<LiveChatSettings> {
  try {
    const docRef = doc(db, COLLECTION_NAME, DOC_ID);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data();
      const merged: LiveChatSettings = { ...DEFAULT_LIVE_CHAT_SETTINGS, ...data };
      cachedSettings = merged;
      try {
        localStorage.setItem('rh_live_chat_settings', JSON.stringify(merged));
      } catch (e) {}
      return merged;
    }
  } catch (err) {
    console.warn('Error fetching Live Chat Settings from Firestore:', err);
  }
  return cachedSettings;
}

export function subscribeToLiveChatSettings(callback: (settings: LiveChatSettings) => void): () => void {
  callback(cachedSettings);
  const docRef = doc(db, COLLECTION_NAME, DOC_ID);
  const unsub = onSnapshot(docRef, (snap) => {
    if (snap.exists()) {
      const data = snap.data();
      const merged: LiveChatSettings = { ...DEFAULT_LIVE_CHAT_SETTINGS, ...data };
      cachedSettings = merged;
      try {
        localStorage.setItem('rh_live_chat_settings', JSON.stringify(merged));
      } catch (e) {}
      callback(merged);
    }
  }, (err) => {
    console.warn('Real-time live chat settings listener error:', err);
  });
  return unsub;
}

export async function saveLiveChatSettingsToFirestore(settings: LiveChatSettings): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, DOC_ID);
  cachedSettings = settings;
  try {
    localStorage.setItem('rh_live_chat_settings', JSON.stringify(settings));
  } catch (e) {}
  await setDoc(docRef, settings, { merge: true });
}
